import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { NAV_ITEMS, SIGN_IN_HREF, START_FREE_HREF } from './content';
import { MkButton, MkContainer, MkLink } from './primitives';

/**
 * Brand mark + text wordmark.
 *
 * The stock icon is deep violet on transparent, which disappears on the dark
 * stage — so this uses the light-stage variant at
 * `public/icons/vaanexa-icon-light.svg` (same artwork, ink recoloured to
 * --mk-ink; the gold accent is unchanged).
 *
 * REPLACE ME: if a real lock-up SVG arrives, drop it at
 * `public/icons/vaanexa-wordmark-light.svg` and swap both elements below for a
 * single <img>. Keep the rendered height at 2rem so the bar does not shift.
 */
function Wordmark(): JSX.Element {
  return (
    <MkLink href="/" className="flex items-center gap-2.5" aria-label="Vaanexa home">
      <img
        src="/icons/vaanexa-icon-light.svg"
        alt=""
        width={40}
        height={32}
        className="h-8 w-auto object-contain"
      />
      <span className="text-lg font-bold tracking-[-0.02em] text-[var(--mk-ink)]">Vaanexa</span>
    </MkLink>
  );
}

export function Navbar(): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A locked body would fight the page scroll, so the mobile sheet closes on
  // navigation instead of trapping the page.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open
          ? 'border-b border-[var(--mk-line)] bg-[rgba(5,6,13,0.72)] backdrop-blur-xl'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <MkContainer className="flex h-16 items-center justify-between gap-6 sm:h-18">
        <Wordmark />

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_ITEMS.map((item) => (
            <MkLink
              key={item.href}
              href={item.href}
              className="text-sm font-medium text-[var(--mk-ink-soft)] transition-colors hover:text-[var(--mk-ink)]"
            >
              {item.label}
            </MkLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <MkButton href={SIGN_IN_HREF} variant="quiet">
            Sign In
          </MkButton>
          <MkButton href={START_FREE_HREF} variant="primary">
            Start Free
          </MkButton>
        </div>

        <button
          type="button"
          className="grid h-10 w-10 place-items-center rounded-xl border border-[var(--mk-line)] text-[var(--mk-ink)] lg:hidden"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls="mk-mobile-nav"
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
        </button>
      </MkContainer>

      {open ? (
        <div
          id="mk-mobile-nav"
          className="border-t border-[var(--mk-line)] bg-[rgba(5,6,13,0.97)] lg:hidden"
        >
          <MkContainer className="flex flex-col gap-1 py-4">
            {NAV_ITEMS.map((item) => (
              <MkLink
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="rounded-xl px-3 py-3 text-base font-medium text-[var(--mk-ink-soft)] hover:bg-[var(--mk-surface)] hover:text-[var(--mk-ink)]"
              >
                {item.label}
              </MkLink>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              <MkButton href={START_FREE_HREF} variant="primary" onClick={() => setOpen(false)}>
                Start Free
              </MkButton>
              <MkButton href={SIGN_IN_HREF} variant="secondary" onClick={() => setOpen(false)}>
                Sign In
              </MkButton>
            </div>
          </MkContainer>
        </div>
      ) : null}
    </header>
  );
}
